import { type z } from 'zod'

export function getPosition<T extends { position?: number | null }>(
	item: T,
): number {
	return item.position ?? 0
}

export function sortByPosition<T extends { position?: number | null }>(
	items: T[],
): T[] {
	return items.sort((a, b) => getPosition(a) - getPosition(b))
}

export const groupEntitiesByField = <T, K extends keyof T>(
	entities: T[],
	field: K & (T[K] extends string ? K : never),
): Record<T[K] & string, T[]> => {
	return entities.reduce(
		(acc, entity) => {
			const key = entity[field] as T[K] & string
			acc[key] = acc[key] ?? []
			acc[key].push(entity)
			return acc
		},
		{} as Record<T[K] & string, T[]>,
	)
}

/**
 * Filters an array of entities by matching a specific field value
 * @param entities - Array of entities to filter
 * @param field - The field key to check against
 * @param value - The value to match on the field
 * @returns Filtered array containing only entities where the field matches the value
 * @example
 * // Returns array of chats with status 'ACTIVE'
 * const activeChats = filterEntitiesByField(chats, 'status', 'ACTIVE')
 *
 * // Returns array of users with type 'ADMIN'
 * const adminUsers = filterEntitiesByField(users, 'type', 'ADMIN')
 */
export const filterEntitiesByField = <T, K extends keyof T>(
	entities: T[],
	field: K,
	value: T[K],
): T[] => {
	return entities.filter((entity) => entity[field] === value)
}

/**
 * Sorts an array of entities by a specified field
 * @param entities - Array of entities to sort
 * @param field - The field key to sort by
 * @param direction - Optional sort direction ('asc' or 'desc'), defaults to 'asc'
 * @returns Sorted array of entities
 * @example
 * // Sort users by name ascending
 * const sortedUsers = sortEntitiesByField(users, 'name')
 *
 * // Sort activities by date descending
 * const sortedActivities = sortEntitiesByField(activities, 'createdAt', 'desc')
 */
export const sortEntitiesByField = <T, K extends keyof T>(
	entities: T[],
	field: K,
	direction: 'asc' | 'desc' = 'asc',
): T[] => {
	return [...entities].sort((a, b) => {
		const aVal = a[field]
		const bVal = b[field]

		if (aVal === bVal) return 0
		if (aVal === null || aVal === undefined) return direction === 'asc' ? -1 : 1
		if (bVal === null || bVal === undefined) return direction === 'asc' ? 1 : -1

		const comparison = aVal < bVal ? -1 : 1
		return direction === 'asc' ? comparison : -comparison
	})
}

/**
 * Sorts an array of entities by a date field
 * @param entities - Array of entities to sort
 * @param field - The date field key to sort by
 * @param direction - Optional sort direction ('asc' or 'desc'), defaults to 'asc'
 * @returns Sorted array of entities
 * @example
 * // Sort activities by scheduledAt ascending
 * const sortedActivities = sortEntitiesByDateField(activities, 'scheduledAt')
 *
 * // Sort messages by createdAt descending
 * const sortedMessages = sortEntitiesByDateField(messages, 'createdAt', 'desc')
 */
export const sortEntitiesByDateField = <T, K extends keyof T>(
	entities: T[],
	field: K,
	direction: 'asc' | 'desc' = 'asc',
): T[] => {
	return [...entities].sort((a, b) => {
		const aVal = a[field] ? new Date(a[field] as string).getTime() : null
		const bVal = b[field] ? new Date(b[field] as string).getTime() : null

		if (aVal === bVal) return 0
		if (aVal === null) return direction === 'asc' ? -1 : 1
		if (bVal === null) return direction === 'asc' ? 1 : -1

		const comparison = aVal < bVal ? -1 : 1
		return direction === 'asc' ? comparison : -comparison
	})
}

// prisma + sqlite means no jsonb support, so we need to parse the json as a string
export const parseEntityJsonField = <T>(
	json: string,
	schema: z.ZodSchema<T>,
): T => {
	return schema.parse(JSON.parse(json))
}
export const stringifyEntityJsonField = <T>(
	entity: T,
	schema: z.ZodSchema<T>,
): string => {
	return JSON.stringify(schema.parse(entity))
}
